import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import CoachDetails from "./CoachDetails";
import { useAxiosInstance } from "../../../services/Interceptor";
import { selectCoach } from "../../../store/slices/coachSlice";
import { getCoaches, selectedCoachAction } from "../../../store/CoachActions";
import CoachesSearchList from "./CoachesSearchList";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight";

const ITEMS_PER_PAGE = 20;
const normalizeText = (value) =>
  value?.toString().toLowerCase().trim().replace(/\s+/g, " ") || "";
const getCoachSearchTerms = (coach) => {
  const firstName = normalizeText(coach?.firstName || coach?.name);
  const middleName = normalizeText(coach?.middleName || coach?.middlename);
  const lastName = normalizeText(coach?.lastName || coach?.lastname);
  return [
    firstName,
    middleName,
    lastName,
    [firstName, middleName, lastName].filter(Boolean).join(" "),
    [firstName, lastName].filter(Boolean).join(" "),
    [lastName, firstName].filter(Boolean).join(" "),
  ].filter(Boolean);
};
function AdminCoaches() {
  const dispatch = useDispatch();
  const coachesState = useSelector((state) => state.coaches);
  const { coaches, loading, error, selectedCoach, coachDetails } = coachesState;
  const apiInstance = useAxiosInstance();
  const [isCoachSelected, setIsCoachSelected] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCoaches, setFilteredCoaches] = useState([]);
  const [visibleCoaches, setVisibleCoaches] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreSentinelRef = useRef(null);

  const { adjustedHeight } = useResponsiveHeight({
    mobileOffset: 290,
    desktopOffset: 230,
    tabletOffset: 260,
  });

  const loadMoreCoaches = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    const nextPage = currentPage + 1;
    const startIndex = nextPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    const nextBatch = filteredCoaches.slice(startIndex, endIndex);

    if (nextBatch.length > 0) {
      setVisibleCoaches((prev) => [...prev, ...nextBatch]);
      setCurrentPage(nextPage);
      setHasMore(endIndex < filteredCoaches.length);
    } else {
      setHasMore(false);
    }

    setIsLoadingMore(false);
  }, [isLoadingMore, hasMore, currentPage, filteredCoaches]);

  const resetPagination = useCallback((nextCoaches) => {
    const firstPage = nextCoaches.slice(0, ITEMS_PER_PAGE);
    setFilteredCoaches(nextCoaches);
    setVisibleCoaches(firstPage);
    setCurrentPage(0);
    setHasMore(nextCoaches.length > ITEMS_PER_PAGE);
  }, []);

  // Fetch selected coach details when selection changes
  useEffect(() => {
    if (selectedCoach) {
      dispatch(selectedCoachAction(apiInstance, selectedCoach.userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCoach, dispatch]);

  useEffect(() => {
    dispatch(getCoaches(apiInstance, ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Keep the filtered list in sync with both the query and incoming data.
  useEffect(() => {
    const normalizedQuery = normalizeText(searchQuery);

    if (!normalizedQuery) {
      resetPagination(coaches || []);
      return;
    }

    const filteredCoaches = (coaches || []).filter((coach) =>
      getCoachSearchTerms(coach).some((value) =>
        value.includes(normalizedQuery),
      ),
    );

    resetPagination(filteredCoaches);
  }, [coaches, resetPagination, searchQuery]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoadingMore &&
          !loading
        ) {
          loadMoreCoaches();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = loadMoreSentinelRef.current;

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoadingMore, loading, loadMoreCoaches]);

  const handleSelectCoach = (coach) => {
    dispatch(selectCoach(coach));
    setIsCoachSelected(true);
  };

  const getCoachId = (coach) => coach?.id || coach?._id || coach?.userId;

  const handleBackButtonClick = (value) => {
    setIsCoachSelected(value);
  };

  return (
    <div className="p-4 bg-gray-100 h-full">
      <div className="flex flex-col xl:flex-row lg:flex-row gap-4">
        <div className="w-full lg:w-2/5 xl:w-2/5">
          <div className="bg-white shadow-lg rounded-lg border-gray-300 h-full p-4 pb-0">
            <h1 className="text-xl mb-3 uppercase">COACHES</h1>
            <CoachesSearchList
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              loading={loading}
              error={error}
              visibleCoaches={visibleCoaches}
              listHeight={adjustedHeight}
              selectedCoach={selectedCoach}
              getCoachId={getCoachId}
              onSelectCoach={handleSelectCoach}
              loadMoreSentinelRef={loadMoreSentinelRef}
              isLoadingMore={isLoadingMore}
              hasMore={hasMore}
            />
          </div>
        </div>

        {/* Coach detail panel */}
        <div className="w-full lg:w-3/5 xl:w-3/5">
          {isCoachSelected ? (
            selectedCoach && coachDetails ? (
              <CoachDetails
                selectedCoach={selectedCoach}
                athletesLiked={coachDetails.athletesLiked}
                backButtonClickEvent={handleBackButtonClick}
              />
            ) : null
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="w-full p-2 flex flex-col items-center justify-center">
                <div className="hidden md:block lg:block text-center">
                  <img
                    alt="Coach"
                    src="/select_row.svg"
                    className="object-cover h-20 pb-3 mx-auto"
                  />
                  <p className="text-center text-[#8E8E93] text-1xl">
                    Please select a coach to view their profile
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminCoaches;
